import liff from "@line/liff";

const API_BASE_URL = "/api/higurashi";
const useMock = process.env.REACT_APP_USE_MOCK === "true";
const mockCharacter = process.env.REACT_APP_HIGURASHI_MOCK_CHARACTER || "rena";
const mockVisualStyle = process.env.REACT_APP_HIGURASHI_MOCK_VISUAL_STYLE || "original";
const mockPhase = process.env.REACT_APP_HIGURASHI_MOCK_PHASE || "3-normal";
const mockIsEnded = process.env.REACT_APP_HIGURASHI_MOCK_IS_ENDED === "true";
const mockCanProceed = process.env.REACT_APP_HIGURASHI_MOCK_CAN_PROCEED === "true";
const mockNextPhaseControl =
  mockCanProceed && mockPhase === "1"
    ? {
        action: "next_phase",
        button_label: "次の章へ進む",
        confirm_title: "次の章に進みますか？",
        warning_text: "次の章に進むと、現在の章には戻れません。",
      }
    : null;

const getIdToken = () => {
  return liff.getIDToken();
};

// Basic Info APIs
export const getBasicInfo = async (accountKey) => {
  if (useMock) {
    return {
      work: "higurashi",
      chapter: "onikakushi",
      lng: "ja",
      character: mockCharacter,
      visualStyle: mockVisualStyle,
      interactionType: "default",
      soulId: "mock-soul-id",
      state: {
        phase: mockPhase,
        is_expired: false,
        is_ended: mockIsEnded,
        expire_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        can_reset: false,
      },
    };
  }
  const token = getIdToken();

  const response = await fetch(
    `${API_BASE_URL}/basic_info?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch basic info");
  }

  const data = await response.json();
  return {
    work: data.work,
    chapter: data.chapter,
    lng: data.language,
    character: data.character,
    visualStyle: data.visual_style,
    interactionType: data.interaction_type,
    soulId: data.soul_id,
    state: data.state,
  };
};

// Tickets APIs
export const listTickets = async (accountKey) => {
  const token = getIdToken();

  const response = await fetch(
    `${API_BASE_URL}/tickets?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch tickets");
  }

  const data = await response.json();

  // dataはlistなので、各要素をコメントアウトされた辞書形式にマップ
  return data.map((item) => ({
    ticketKey: item.data?.data?.ticket_key,
    ticketType: item.ticket_type,
    name: item.name,
    description: item.description,
    notice: item.notice,
    displayPrice: item.display_price,
    data: item.data,
  }));
};

// Email APIs
export const getUserEmail = async () => {
  const token = getIdToken();

  const response = await fetch(`/api/users/email`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
      "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user");
  }

  const data = await response.json();
  return {
    emailAddress: data.email_address,
    disableMailDelivery: data.disable_mail_delivery,
  };
};

export const getUserLineEmail = async () => {
  const token = getIdToken();
  try {
    const response = await fetch("https://api.line.me/oauth2/v2.1/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        id_token: token,
        client_id: process.env.REACT_APP_LINE_LOGIN_CHANNEL_ID,
      }),
    });

    const data = await response.json();

    if ("email" in data) {
      return data.email;
    } else {
      console.warn("email not found in response:", data);
      return "";
    }
  } catch (error) {
    console.error("Failed to fetch email:", error);
    return ""; // 失敗時も空文字を返す
  }
};

export const updateUserEmail = async (emailAddress, disableMailDelivery) => {
  const token = getIdToken();
  const response = await fetch(`/api/users/email`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email_address: emailAddress,
      disable_mail_delivery: disableMailDelivery,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to post email address");
  }
  return response.json();
};

// My Page APIs
export const getMyPageData = async (accountKey) => {
  if (useMock) {
    return {
      remaining_days: 5,
      expire_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      can_reset: false,
      can_proceed_to_next_phase: mockCanProceed,
      next_phase_control: mockNextPhaseControl,
      is_expired: false,
      is_ended: mockIsEnded,
      stories: {
        current: { phase: mockPhase, order: 3, title: "", content: "" },
        previous: [],
      },
    };
  }
  const token = getIdToken();

  const response = await fetch(
    `${API_BASE_URL}/state?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch my page data");
  }

  return response.json();
};

// Quests APIs

const _to_quest = (data) => {
  return {
    key: data.quest_key,
    name: data.name,
    description: data.description,
    isCleared: data.is_cleared,
    isLocked: !data.is_unlocked,
  };
};

const _to_phase_quest = (data) => {
  return {
    phase: {
      order: data.phase.order,
      title: data.phase.title,
    },
    quests: data.quests.map(_to_quest),
  };
};
export const listQuests = async (accountKey) => {
  if (useMock) {
    const mockData = {
      current: {
        phase: { order: 2, title: "綿流しの夜" },
        quests: [
          {
            quest_key: "q_cleared",
            name: "古手神社を訪れる",
            description: "梨花と再会し、神社の言い伝えを聞く。",
            is_cleared: true,
            is_unlocked: true,
          },
          {
            quest_key: "q_open",
            name: "鬼ヶ淵沼を調べる",
            description: "村の言い伝えを確かめるため、沼のほとりへ向かう。\n何かが見つかるかもしれない。",
            is_cleared: false,
            is_unlocked: true,
          },
          {
            quest_key: "q_locked_1",
            name: "未解放のクエスト",
            description: "まだ語られていない物語。",
            is_cleared: false,
            is_unlocked: false,
          },
          {
            quest_key: "q_locked_2",
            name: "深淵の真相",
            description: "封じられた記憶。",
            is_cleared: false,
            is_unlocked: false,
          },
        ],
      },
      previous: [
        {
          phase: { order: 1, title: "綿流しの夕" },
          quests: [
            {
              quest_key: "q_prev_1",
              name: "雛見沢へ到着する",
              description: "新しい村での最初の一歩。",
              is_cleared: true,
              is_unlocked: true,
            },
          ],
        },
      ],
    };
    return {
      current: _to_phase_quest(mockData.current),
      previous: mockData.previous.map(_to_phase_quest),
    };
  }
  const token = getIdToken();

  const response = await fetch(
    `${API_BASE_URL}/quests?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch quest list");
  }
  const data = await response.json();
  return {
    current: _to_phase_quest(data.current),
    previous: data.previous.map(_to_phase_quest),
  };
};

export const listHints = async (accountKey) => {
  const token = getIdToken();

  const response = await fetch(
    `${API_BASE_URL}/hints?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`, // トークンをAuthorizationヘッダーに追加
        "Content-Type": "application/json", // 必要に応じてContent-Typeを設定
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch hint list");
  }
  const data = await response.json();
  return data.map((item) => ({
    key: item.key,
    name: item.name,
    description: item.description,
  }));
};

// ==============================================
// Stripe  APIs
// ==============================================
export const createStripeSession = async (purchaseData) => {
  const token = getIdToken();

  const jsonString = JSON.stringify(purchaseData);
  const blob = new Blob([jsonString], { type: "application/json" });
  const formData = new FormData();
  formData.append("data", blob, "payload.json");

  const response = await fetch(`${API_BASE_URL}/stripe/checkout_sessions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to create stripe session");
  }
  const data = await response.json();
  return data.session_id;
};

// ==============================================
// PayPay  APIs
// ==============================================
export const createPayPayQrCode = async (purchaseData) => {
  const token = getIdToken();

  const jsonString = JSON.stringify(purchaseData);
  const blob = new Blob([jsonString], { type: "application/json" });

  const formData = new FormData();
  // formData.append("product_type", data.product_type);
  formData.append("data", blob, "payload.json");

  const response = await fetch(`${API_BASE_URL}/paypay/qrcode`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to create paypay qr code");
  }
  return response.json();
};

export const getUnconfirmedPayPayPayment = async (accountKey) => {
  const token = getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/paypay/unconfirmed?account_key=${accountKey}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch unconfirmed payment");
  }
  const data = await response.json();
  return { merchantPaymentId: data.merchant_payment_id };
};

// ==============================================
// Guidelines APIs
// ==============================================
export const getGuidelinesAgreementStatus = async (accountKey) => {
  const token = getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/guidelines?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch guidelines agreement status");
  }
  return response.json();
};

export const postGuidelinesAgreement = async (accountKey) => {
  const token = getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/guidelines/agree?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to post guidelines agreement");
  }
  return response.json();
};

// ==============================================
// Phase APIs
// ==============================================
export const postAdvancePhase = async (accountKey) => {
  const token = getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/advance-phase?account_key=${encodeURIComponent(accountKey)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    let detail = "";
    try {
      const errBody = await response.json();
      detail = errBody?.detail || "";
    } catch (_) {
      // body may be empty / non-json — leave detail blank
    }
    const error = new Error(
      detail || `Failed to advance phase (status ${response.status})`
    );
    error.status = response.status;
    error.detail = detail;
    throw error;
  }
  return response.json();
};

export const pollPayPayPaymentStatus = async (merchantPaymentId) => {
  const token = getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/paypay/poll?merchant_payment_id=${merchantPaymentId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch payment status");
  }
  const data = await response.json();
  return { status: data.status };
};
